/**
 * Engagement — Page performance, events, and user behavior
 * Data: GA4 Property 484092824 (Feb 26 – Mar 25, 2026)
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInUp, pageTransition, pageInitial, pageAnimate } from '@/lib/motionVariants';
import { formatNumber } from '@/lib/formatting';
import { TEXT, BORDER, BRAND, CHART_COLORS, getChartTooltip } from '../charts/utils';
import { useChatStore } from '@/stores/chatStore';
import { useThemeKey } from '@/hooks/useThemeKey';
import PageDrillPanel from '../drill/PageDrillPanel';
import type { PageData } from '../drill/PageDrillPanel';
import DeltaIndicator from '../shared/DeltaIndicator';
import { ENGAGEMENT_KPIS, PAGE_COMPARISON } from '@/data/comparisonData';
import PageHeader from '../shared/PageHeader';
import { useComparisonPeriods } from '@/hooks/useComparisonPeriods';
import { usePages, useEvents } from '@/hooks/useGA4Data';

const askChat = (query: string, e?: React.MouseEvent) => {
  if (e) {
    useChatStore.getState().showAnalyzePrompt(
      query, '', (e.currentTarget as HTMLElement).getBoundingClientRect(),
    );
  } else {
    useChatStore.getState().sendAnalyzeQuery(query);
  }
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const TOP_PAGES = [
  { page: 'Homepage',       views: 133_000, pctOfTotal: 50.0 },
  { page: 'Dining',         views: 49_000,  pctOfTotal: 18.4 },
  { page: 'Aleteo',         views: 27_000,  pctOfTotal: 10.2 },
  { page: 'Oak & Amber',    views: 19_000,  pctOfTotal: 7.1 },
  { page: 'Amenities',      views: 8_500,   pctOfTotal: 3.2 },
  { page: 'Accommodations', views: 7_000,   pctOfTotal: 2.6 },
  { page: 'Nectarie Cafe',  views: 4_900,   pctOfTotal: 1.8 },
];

const TOP_EVENTS = [
  { event: 'page_view',       count: 266_000, pct: 47.7 },
  { event: 'session_start',   count: 89_000,  pct: 16.0 },
  { event: 'first_visit',     count: 71_000,  pct: 12.7 },
  { event: 'user_engagement', count: 63_000,  pct: 11.3 },
  { event: 'click',           count: 23_000,  pct: 4.1 },
  { event: 'scroll',          count: 20_000,  pct: 3.6 },
  { event: 'nav_reservation_click', count: 13_000, pct: 2.3 },
];

const Engagement: React.FC = () => {
  const themeKey = useThemeKey();
  const chartTooltip = getChartTooltip();
  const { compareEnabled, popTooltip, yoyTooltip } = useComparisonPeriods();

  // Live API data with hardcoded fallbacks
  const { data: livePages } = usePages();
  const { data: liveEvents } = useEvents();

  const pageRows = livePages?.map(p => ({
    page: p.page,
    views: p.views,
    pctOfTotal: p.pctOfTotal,
  })) ?? TOP_PAGES;

  const eventRows = liveEvents ?? TOP_EVENTS;

  // Page drill panel state
  const [selectedPage, setSelectedPage] = useState<PageData | null>(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const handlePageClick = useCallback((page: typeof TOP_PAGES[number]) => {
    setSelectedPage(page);
    setDrillOpen(true);
  }, []);
  const handleDrillClose = useCallback(() => {
    setDrillOpen(false);
    setSelectedPage(null);
  }, []);
  const pagesBarData = useMemo(() => ({
    labels: pageRows.map((p) => p.page),
    datasets: [{
      label: 'Page Views',
      data: pageRows.map((p) => p.views),
      backgroundColor: pageRows.map((_, i) => i === 0 ? BRAND.gold : CHART_COLORS[2]),
      borderRadius: 4,
    }],
  }), [pageRows]);

  const eventsBarData = useMemo(() => ({
    labels: eventRows.map((e) => e.event),
    datasets: [{
      label: 'Event Count',
      data: eventRows.map((e) => e.count),
      backgroundColor: eventRows.map((_, i) => i === eventRows.length - 1 ? BRAND.gold : CHART_COLORS[2]),
      borderRadius: 4,
    }],
  }), [eventRows]);

  return (
    <motion.div className="space-y-6" initial={pageInitial} animate={pageAnimate} transition={pageTransition}>
      <PageHeader title="Engagement" subtitle="Page performance, user events, and content interaction" />

      {/* Summary KPIs */}
      <motion.div className="grid grid-cols-2 md:grid-cols-5 gap-3" variants={staggerContainer} initial="hidden" animate="visible">
        <motion.div variants={fadeInUp}>
        <div className="bg-bg-1 border border-border rounded-xl p-4">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Pageviews</p>
          <p className="font-mono text-lg font-bold text-text">266K</p>
          {compareEnabled && <div className="mt-1"><DeltaIndicator current={ENGAGEMENT_KPIS.pageviews.current} yoyPrior={ENGAGEMENT_KPIS.pageviews.yoy} popPrior={ENGAGEMENT_KPIS.pageviews.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        </div>
        </motion.div>
        <motion.div variants={fadeInUp}>
        <div className="bg-bg-1 border border-border rounded-xl p-4">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Avg Eng. Time</p>
          <p className="font-mono text-lg font-bold text-text">23s</p>
          {compareEnabled && <div className="mt-1"><DeltaIndicator current={ENGAGEMENT_KPIS.avgEngagementTime.current} yoyPrior={ENGAGEMENT_KPIS.avgEngagementTime.yoy} popPrior={ENGAGEMENT_KPIS.avgEngagementTime.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        </div>
        </motion.div>
        <motion.div variants={fadeInUp}>
        <div className="bg-bg-1 border border-monarch-gold/40 rounded-xl p-4">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Eng. Rate</p>
          <p className="font-mono text-lg font-bold text-monarch-gold">79.5%</p>
          {compareEnabled && <div className="mt-1"><DeltaIndicator current={ENGAGEMENT_KPIS.engagementRate.current} yoyPrior={ENGAGEMENT_KPIS.engagementRate.yoy} popPrior={ENGAGEMENT_KPIS.engagementRate.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        </div>
        </motion.div>
        <motion.div variants={fadeInUp}>
        <div className="bg-bg-1 border border-border rounded-xl p-4">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Total Events</p>
          <p className="font-mono text-lg font-bold text-text">557K</p>
          {compareEnabled && <div className="mt-1"><DeltaIndicator current={ENGAGEMENT_KPIS.totalEvents.current} yoyPrior={ENGAGEMENT_KPIS.totalEvents.yoy} popPrior={ENGAGEMENT_KPIS.totalEvents.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        </div>
        </motion.div>
        <motion.div variants={fadeInUp}>
        <div className="bg-bg-1 border border-border rounded-xl p-4">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Events/Session</p>
          <p className="font-mono text-lg font-bold text-text">6.36</p>
          {compareEnabled && <div className="mt-1"><DeltaIndicator current={ENGAGEMENT_KPIS.eventsPerSession.current} yoyPrior={ENGAGEMENT_KPIS.eventsPerSession.yoy} popPrior={ENGAGEMENT_KPIS.eventsPerSession.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        </div>
        </motion.div>
      </motion.div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm text-text-2 mb-4">Top Pages by Views</h3>
          <div style={{ height: 300 }}>
            <Bar
              key={`pages-bar-${themeKey}`}
              data={pagesBarData}
              options={{
                responsive: true, maintainAspectRatio: false, indexAxis: 'y',
                scales: {
                  x: { grid: { color: BORDER.default }, ticks: { color: TEXT.faint, font: { size: 10 }, callback: (v) => formatNumber(Number(v)) }, border: { display: false } },
                  y: { grid: { display: false }, ticks: { color: TEXT.muted, font: { size: 11 } }, border: { display: false } },
                },
                plugins: { legend: { display: false }, tooltip: chartTooltip },
              }}
            />
          </div>
        </div>
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm text-text-2 mb-4">Top Events</h3>
          <div style={{ height: 300 }}>
            <Bar
              key={`events-bar-${themeKey}`}
              data={eventsBarData}
              options={{
                responsive: true, maintainAspectRatio: false, indexAxis: 'y',
                scales: {
                  x: { grid: { color: BORDER.default }, ticks: { color: TEXT.faint, font: { size: 10 }, callback: (v) => formatNumber(Number(v)) }, border: { display: false } },
                  y: { grid: { display: false }, ticks: { color: TEXT.muted, font: { size: 10, family: "'JetBrains Mono', monospace" } }, border: { display: false } },
                },
                plugins: { legend: { display: false }, tooltip: chartTooltip },
              }}
            />
          </div>
        </div>
      </div>

      {/* Pages Table */}
      <div className="bg-bg-1 border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-display text-sm text-text-2">Page Performance Detail</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-bg-2 text-text-3">
                <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Page</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Views</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">% of Total</th>
                {compareEnabled && <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Change</th>}
                <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Distribution</th>
                <th className="px-4 py-3 w-8" />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, idx) => (
                <tr key={idx} className="group border-b border-border/50 hover:bg-bg-2 transition-colors cursor-pointer" onClick={() => handlePageClick(row)}>
                  <td className="px-4 py-3 font-medium text-text">{row.page}</td>
                  <td className="px-4 py-3 text-right font-mono text-text">{formatNumber(row.views)}</td>
                  <td className="px-4 py-3 text-right font-mono text-text-2">{row.pctOfTotal}%</td>
                  {compareEnabled && (
                    <td className="px-4 py-3 text-right">
                      {PAGE_COMPARISON[row.page] && (
                        <DeltaIndicator current={row.views} yoyPrior={PAGE_COMPARISON[row.page].yoy} popPrior={PAGE_COMPARISON[row.page].pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="w-full h-3 bg-bg-2 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${row.pctOfTotal}%`,
                          backgroundColor: idx === 0 ? BRAND.gold : CHART_COLORS[2],
                        }}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-text-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-monarch-gold/5 border border-monarch-gold/30 rounded-xl p-4 cursor-pointer hover:brightness-110 transition-all" onClick={(e) => askChat('Dining pages account for 37% of non-homepage views. How can we leverage this gateway content to drive bookings?', e)}>
          <p className="text-sm font-semibold text-text mb-1">Dining is the Gateway Content</p>
          <p className="text-xs text-text-3 leading-relaxed">
            Dining-related pages (Dining, Aleteo, Oak & Amber, Nectarie Cafe) account for 37% of all non-homepage views (99,900 combined). Guests are researching restaurants before booking. This is a content marketing opportunity — drive organic traffic through F&B content.
          </p>
        </div>
        <div className="bg-warning/5 border border-warning/30 rounded-xl p-4 cursor-pointer hover:brightness-110 transition-all" onClick={(e) => askChat('Accommodations page has only 7K views (2.6%) despite being the booking page. How do we drive more traffic here from dining pages?', e)}>
          <p className="text-sm font-semibold text-text mb-1">Accommodations Page Under-Indexed</p>
          <p className="text-xs text-text-3 leading-relaxed">
            Accommodations has only 7K views (2.6%) despite being the core booking page. Dining gets 7x more views. The path from dining interest to room booking may not be clear enough. Consider adding room upsells on dining pages.
          </p>
        </div>
      </div>

      {/* Page Drill Panel */}
      <PageDrillPanel pageData={selectedPage} open={drillOpen} onClose={handleDrillClose} />
    </motion.div>
  );
};

export default Engagement;
